import type { Prompt } from "@webuild-ai/admin/models/components";

import { ScrollShadow, Spinner, Tooltip, TooltipTrigger, TooltipContent } from "@heroui/react";
import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { ModalOverlay, ModalHeader, ModalBody } from "@/components/UI/ModalOverlay";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { blurActiveElement } from "@/utils/dom";
import { logger } from "@/telemetry";
import { fetchAllPages } from "@/utils/paginate";

/** Delay before a prompt search fires after the user stops typing. */
export const SEARCH_DEBOUNCE_MS = 500;

interface PromptSuggestion {
  id: string;
  title: string;
  description: string;
  content: string;
}

interface PromptSearchModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectTemplate: (template: string) => void;
}

export function PromptSearchModal({
  isOpen,
  onOpenChange,
  onSelectTemplate,
}: PromptSearchModalProps) {
  const [templateInput, setTemplateInput] = useState("");
  const [suggestions, setSuggestions] = useState<PromptSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearchPending, setIsSearchPending] = useState(false);
  const { adminClient } = useWebuildAPIClient();
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  // Monotonically increasing token used to discard stale searchTemplates responses
  // when the user fires a newer search before an older one resolves.
  const latestQueryRef = useRef(0);
  const { t } = useTranslation();

  // Highlighted suggestion for keyboard navigation (arrow keys + Enter to select).
  const [selectedIndex, setSelectedIndex] = useState(0);
  const suggestionsListboxId = "prompt-suggestions-listbox";

  // Callback ref: focuses the input the moment it mounts, then re-asserts on
  // the next frame in case ModalOverlay's focus trap moves focus elsewhere.
  // We deliberately do not also set `autoFocus` — having two competing focus
  // mechanisms invites a future maintainer to delete one assuming it's redundant.
  const setSearchInputRef = useCallback((el: HTMLInputElement | null) => {
    searchInputRef.current = el;
    if (el) {
      el.focus();
      requestAnimationFrame(() => {
        if (searchInputRef.current && document.activeElement !== searchInputRef.current) {
          searchInputRef.current.focus();
        }
      });
    }
  }, []);

  const searchTemplates = async (searchText: string) => {
    // Claim a token for this call; any later call increments it. When the awaited
    // response resolves we compare against the latest token and drop stale results.
    const queryId = ++latestQueryRef.current;

    setIsLoading(true);

    logger.info("searchTemplates", { searchText });
    try {
      setSuggestions([]);
      const response = await adminClient?.prompts.search({ searchText });

      if (queryId !== latestQueryRef.current) {
        return;
      }

      if (!response) {
        logger.error("No response from adminClient?.prompts.generate");
        setSuggestions([]);

        return;
      }
      setSuggestions(
        response.map((prompt) => ({
          id: prompt.id,
          title: prompt.name,
          description: prompt.description,
          content: prompt.content,
        })),
      );
    } catch (error) {
      logger.error("Error searching templates", error);
    } finally {
      if (queryId === latestQueryRef.current) {
        setIsLoading(false);
      }
    }
  };

  const fetchInitialPrompts = useCallback(async () => {
    setIsLoading(true);
    try {
      setSuggestions([]);
      if (!adminClient) return;

      // Every page, since the initial list is the whole picker until the user
      // types a query; the server's default order (own prompts first) is kept.
      const { items } = await fetchAllPages<Prompt>((page) =>
        adminClient.prompts.listPrompts(page),
      );

      setSuggestions(
        items.map((prompt) => ({
          id: prompt.id,
          title: prompt.name,
          description: prompt.description,
          content: prompt.content,
        })),
      );
    } catch (error) {
      logger.error("Error fetching initial prompts", error);
    } finally {
      setIsLoading(false);
    }
  }, [adminClient]);

  // Handle modal close with focus cleanup
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      blurActiveElement();
    }
    onOpenChange(open);
  };

  const handleTemplateSelect = (template: string) => {
    onSelectTemplate(template);
    handleOpenChange(false);
  };

  useEffect(() => {
    if (isOpen) {
      setTemplateInput("");
      setIsSearchPending(false);
      fetchInitialPrompts();
    } else {
      // Modal stays mounted across close→open cycles; clear any in-flight pending
      // state so a half-typed search from the previous session doesn't leak.
      setIsSearchPending(false);
    }

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
    };
  }, [isOpen, fetchInitialPrompts]);

  // Reset the highlighted suggestion whenever the result set changes.
  useEffect(() => {
    setSelectedIndex(suggestions.length > 0 ? 0 : -1);
  }, [suggestions]);

  // Keep the highlighted suggestion scrolled into view.
  useEffect(() => {
    if (selectedIndex < 0) return;
    const container = document.getElementById(suggestionsListboxId);
    const el = container?.querySelector(`[data-option-index="${selectedIndex}"]`);

    (el as HTMLElement | null)?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  if (!isOpen) return null;

  return (
    <ModalOverlay isOpen={isOpen} size="2xl" onOpenChange={handleOpenChange}>
      <ModalHeader className="flex flex-col gap-1" data-id="prompt-search-modal-header">
        {t("Prompts")}
      </ModalHeader>
      <ModalBody>
        <div className="flex flex-col gap-4">
          <input
            ref={setSearchInputRef}
            aria-activedescendant={
              selectedIndex >= 0 && suggestions[selectedIndex]
                ? `prompt-suggestion-option-${suggestions[selectedIndex].id}`
                : undefined
            }
            aria-autocomplete="list"
            aria-controls={suggestionsListboxId}
            aria-expanded={suggestions.length > 0}
            aria-label={t("Search for a prompt")}
            className="w-full px-3 py-1.5 text-sm rounded-lg bg-default-100 text-foreground placeholder:text-default-500 border border-default-200 focus:outline-none focus-visible:ring-1 focus-visible:ring-primary-500 focus-visible:border-primary-500"
            data-id="prompt-search-input"
            placeholder={t("Search for a prompt...")}
            role="combobox"
            type="text"
            value={templateInput}
            onChange={(e) => {
              const val = e.target.value;

              setTemplateInput(val);
              if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
              }
              if (val.trim()) {
                setIsSearchPending(true);
                debounceTimeoutRef.current = setTimeout(() => {
                  setIsSearchPending(false);
                  searchTemplates(val);
                }, SEARCH_DEBOUNCE_MS);
              } else {
                setIsSearchPending(false);
                setSuggestions([]);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                if (suggestions.length > 0) {
                  setSelectedIndex((i) => (i < suggestions.length - 1 ? i + 1 : Math.max(i, 0)));
                }

                return;
              }
              if (e.key === "ArrowUp") {
                e.preventDefault();
                if (suggestions.length > 0) {
                  setSelectedIndex((i) => (i > 0 ? i - 1 : 0));
                }

                return;
              }
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                // A search is queued from typing: run it now rather than selecting stale results.
                if (isSearchPending && templateInput.trim()) {
                  if (debounceTimeoutRef.current) {
                    clearTimeout(debounceTimeoutRef.current);
                  }
                  setIsSearchPending(false);
                  searchTemplates(templateInput);

                  return;
                }
                // Otherwise select the highlighted suggestion.
                if (
                  suggestions.length > 0 &&
                  selectedIndex >= 0 &&
                  selectedIndex < suggestions.length
                ) {
                  handleTemplateSelect(suggestions[selectedIndex].content);
                }
              }
            }}
          />
          <div className="flex flex-col gap-2">
            <div
              aria-live="polite"
              className="flex items-center gap-2"
              data-id="prompt-search-status"
              role="status"
            >
              {(isLoading || isSearchPending) && (
                <Spinner aria-hidden="true" color="current" size="sm" />
              )}
              <h3 className="text-sm font-medium">
                {isLoading || isSearchPending
                  ? t("Searching for prompts...")
                  : suggestions.length > 0
                    ? t("Suggested Prompts:")
                    : ""}
              </h3>
            </div>
            <ScrollShadow
              aria-label={t("Suggested prompts")}
              className="w-full max-h-[50vh] overflow-y-auto flex flex-col gap-2 px-1.5 py-1.5"
              data-id="prompt-suggestions-container"
              id={suggestionsListboxId}
              role="listbox"
            >
              {suggestions.map((suggestion, index) => {
                const isHighlighted = index === selectedIndex;
                const card = (
                  <div
                    key={suggestion.id}
                    aria-selected={isHighlighted}
                    className={`flex flex-col items-start px-4 py-3 rounded-2xl cursor-pointer border shrink-0 transition-[border-color,box-shadow,transform] duration-150 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-content1 motion-reduce:transition-none ${
                      isHighlighted
                        ? "bg-default-100 border-primary-500"
                        : "bg-default-100 border-default-200 hover:border-default-300"
                    }`}
                    data-id={`prompt-suggestion-${suggestion.id}`}
                    data-option-index={index}
                    id={`prompt-suggestion-option-${suggestion.id}`}
                    role="option"
                    tabIndex={0}
                    onClick={() => handleTemplateSelect(suggestion.content)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleTemplateSelect(suggestion.content);
                      }
                    }}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <span
                      className="text-sm font-semibold text-foreground line-clamp-1"
                      data-id={`prompt-suggestion-title-${suggestion.id}`}
                    >
                      {suggestion.title}
                    </span>
                    {suggestion.description && (
                      <span
                        className="text-xs text-default-500 line-clamp-1 mt-0.5"
                        data-id={`prompt-suggestion-description-${suggestion.id}`}
                      >
                        {suggestion.description}
                      </span>
                    )}
                    <span
                      className="text-xs text-default-600 whitespace-pre-wrap break-words line-clamp-2 mt-1.5"
                      data-id={`prompt-suggestion-content-${suggestion.id}`}
                    >
                      {suggestion.content}
                    </span>
                  </div>
                );

                return (
                  <Tooltip key={suggestion.id}>
                    {/*
                      HeroUI's TooltipTrigger renders its own focusable
                      `<div role="button" tabindex="0">` wrapper. Combined with the
                      card's own tabIndex={0} that produced two tab stops per card
                      (the wrapper's square focus outline, then the card's rounded
                      ring). tabIndex={-1} drops the wrapper from the tab order so the
                      card is the single, correctly-styled focus target. The tooltip
                      still opens on hover.
                    */}
                    <TooltipTrigger tabIndex={-1}>{card}</TooltipTrigger>
                    <TooltipContent placement="right">
                      <div
                        className="max-w-[420px] max-h-[60vh] overflow-y-auto text-xs leading-relaxed whitespace-pre-wrap break-normal hyphens-auto flex flex-col"
                        data-id={`prompt-suggestion-tooltip-${suggestion.id}`}
                      >
                        <span className="text-sm font-semibold">{suggestion.title}</span>
                        {suggestion.description && (
                          <span className="opacity-70 mt-1">{suggestion.description}</span>
                        )}
                        <span className="mt-2 pt-2 border-t border-current/15">
                          {suggestion.content}
                        </span>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </ScrollShadow>
          </div>
        </div>
      </ModalBody>
    </ModalOverlay>
  );
}
